import React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Tab, Tabs } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'

import AgentPage from '../../components/templates/AgentPage'
import ConversationsTab from './ConversationsTab'
import TasksTab from './TasksTab'
import PlannerTab from './PlannerTab'
import HomeTab from './HomeTab'
import RemindersTab from './RemindersTab'
import NotificationsTab from './NotificationsTab'

import { ACCOUNT_KEY } from '../../providers/AccountProvider'
import { useAuth } from '../../providers/AuthProvider'
import useStyles from './styles'

const TAB_HOME = 0
const TAB_PLANNER = 1
const TAB_REMINDERS = 2
const TAB_NOTIFICATIONS = 3
const TAB_TASKS = 4
const TAB_CONVERSATIONS = 5
const TAB_STORAGE_KEY = 'LAST_DASHBOARD_TAB'

const getTab = searchParams => {
  if (searchParams.has('tab')) {
    const tab = searchParams.get('tab')
    if (tab === 'planner') {
      return TAB_PLANNER
    }
    return +(localStorage.getItem(TAB_STORAGE_KEY) ?? TAB_HOME)
  }
  return +(localStorage.getItem(TAB_STORAGE_KEY) ?? TAB_HOME)
}

const Dashboard: React.FC = () => {
  const { isLoggedIn } = useAuth()
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const { logout, isAuthenticated, isLoading } = useAuth0()
  const [tab, setTab] = React.useState(getTab(searchParams))
  const containerRef = React.useRef(null)

  React.useEffect(() => {
    localStorage.setItem(TAB_STORAGE_KEY, tab.toString())
  }, [tab])

  const classes = useStyles()

  // force logout if no user is logged in
  React.useEffect(() => {
    if (isLoading) return
    if (!isAuthenticated) {
      localStorage.removeItem('session_user')
      localStorage.removeItem('session_token')
      localStorage.removeItem('version')
      localStorage.removeItem(ACCOUNT_KEY)
      logout({ logoutParams: { returnTo: `${window.location.origin}/login` } })
    }
  }, [isLoggedIn, isLoading, isAuthenticated, logout])

  return (
    <AgentPage rootRef={containerRef} scroll>
      <div className={classes.root}>
        <Tabs
          variant='scrollable'
          value={tab}
          onChange={(_, val) => setTab(val)}
          className={classes.tabs}
        >
          <Tab label='Home' classes={{ root: classes.tab }} />

          <Tab label='Planner' classes={{ root: classes.tab }} />

          <Tab label='Reminders' classes={{ root: classes.tab }} />

          <Tab label='Notifications' classes={{ root: classes.tab }} />

          <Tab label='Tasks' classes={{ root: classes.tab }} />

          <Tab label='Conversations' classes={{ root: classes.tab }} />
        </Tabs>

        <AnimatePresence initial={false} mode='wait'>
          <motion.div
            key={tab}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            {tab === TAB_HOME && <HomeTab />}

            {tab === TAB_PLANNER && <PlannerTab />}

            {tab === TAB_REMINDERS && <RemindersTab />}

            {tab === TAB_NOTIFICATIONS && (
              <NotificationsTab rootRef={containerRef} />
            )}

            {tab === TAB_TASKS && <TasksTab />}

            {tab === TAB_CONVERSATIONS && <ConversationsTab />}
          </motion.div>
        </AnimatePresence>
      </div>
    </AgentPage>
  )
}

export default Dashboard
